// SCOPE CHIP — the visible face of selection-aware styling (R0 shell).
//
// CONCEPT-text-ribbon §2.3: Row 1's left edge always says what you're about
// to style — "All text" / word / N letters — and clicking it offers to WIDEN
// the scope. R0 is a pure presentational shell: scope arrives as a prop,
// widening fires a callback. R2 plumbs the live selection mirrors
// (hasCharSelection / selectionStyle vocabulary) behind the same props.
//
// Props contract (R2 wires):
//   scope: { kind: 'all' }                          → "All text"
//          { kind: 'word', word: 'Read' }           → 'Word: "Read"'
//          { kind: 'chars', count: 3 }              → "3 letters"
//   onSetScope(kind) — 'word' | 'all' (widening only; narrowing stays a
//   canvas gesture — you narrow by highlighting, the chip never clones that).
import React, { useEffect, useRef, useState } from 'react';

export function scopeLabel(scope) {
  if (!scope || scope.kind === 'all') return 'All text';
  if (scope.kind === 'word') return `Word: “${scope.word ?? '…'}”`;
  if (scope.kind === 'chars') {
    const n = scope.count ?? 0;
    return `${n} letter${n === 1 ? '' : 's'}`;
  }
  return 'All text';
}

export default function ScopeChip({ scope, onSetScope }) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef(null);
  const kind = scope?.kind || 'all';

  useEffect(() => {
    if (!open) return undefined;
    const onDocDown = (ev) => {
      if (rootRef.current && !rootRef.current.contains(ev.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onDocDown);
    return () => document.removeEventListener('mousedown', onDocDown);
  }, [open]);

  // Widening menu depends on where you are: chars → word or all; word → all;
  // all → nothing to widen (chip is informative, not clickable-dead — it
  // still explains itself).
  const widenItems = kind === 'chars'
    ? [{ kind: 'word', label: 'Style the whole word' }, { kind: 'all', label: 'Style all the text' }]
    : kind === 'word'
      ? [{ kind: 'all', label: 'Style all the text' }]
      : [];

  const pick = (k) => {
    setOpen(false);
    onSetScope?.(k);
  };

  return (
    <span className="rbn-anchor" ref={rootRef}>
      <button
        type="button"
        className={`rbn-scope-chip rbn-scope-${kind}`}
        data-scope-kind={kind}
        aria-expanded={open}
        title={kind === 'all'
          ? 'Your styling changes the whole text box'
          : 'Your styling changes just this part — click to widen'}
        onClick={() => setOpen((o) => !o)}
      >
        <span className="rbn-scope-dot" aria-hidden="true" />
        <span className="rbn-scope-label">{scopeLabel(scope)}</span>
      </button>
      {open && (
        <div className="rbn-menu rbn-scope-menu" role="menu" aria-label="Styling scope">
          <div className="rbn-menu-title">Styling scope</div>
          <div className="rbn-scope-current">
            Now styling: <strong>{scopeLabel(scope)}</strong>
          </div>
          {widenItems.map((it) => (
            <button
              key={it.kind}
              type="button"
              className="rbn-menu-action"
              data-scope-widen={it.kind}
              onClick={() => pick(it.kind)}
            >
              {it.label}
            </button>
          ))}
          {widenItems.length === 0 && (
            <div className="rbn-scope-hint">Highlight letters on the canvas to style just part of it.</div>
          )}
        </div>
      )}
    </span>
  );
}
